import { PlayIcon, VideoOffIcon } from "lucide-react";
import { memo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useArtifactUrl } from "@/lib/artifact-cache";
import { cn } from "@/lib/utils";
import { useRecord } from "@/sync/hooks";

/**
 * Video artifact: shows the cached poster until played, then loads the original from the blob
 * cache (so a canvas full of clips doesn't download every file up front).
 */
export const ArtifactVideo = memo(function ArtifactVideo({
	artifactId,
	className,
	autoLoad = false,
}: {
	artifactId: string;
	className?: string;
	autoLoad?: boolean;
}) {
	const record = useRecord("artifact", artifactId);
	const [requested, setRequested] = useState(autoLoad);
	const artifact = {
		id: artifactId,
		kind: record?.kind ?? "video",
		thumbnailR2Key: record?.thumbnailR2Key,
	};
	const poster = useArtifactUrl(artifact, "thumb");
	const video = useArtifactUrl(requested ? artifact : null, "original");

	if (video.error || (poster.error && !requested)) {
		return (
			<div
				className={cn("flex items-center justify-center bg-muted text-muted-foreground", className)}
			>
				<VideoOffIcon className="size-4" />
			</div>
		);
	}
	if (requested && video.url) {
		return (
			// biome-ignore lint/a11y/useMediaCaption: generated clips have no caption track
			<video
				src={video.url}
				poster={poster.url}
				controls
				autoPlay={!autoLoad}
				playsInline
				loop
				className={cn("nodrag nowheel bg-black object-contain", className)}
			/>
		);
	}
	return (
		<button
			type="button"
			aria-label="Play video"
			onClick={() => setRequested(true)}
			className={cn("nodrag group relative overflow-hidden bg-muted", className)}
		>
			{poster.url ? (
				<img src={poster.url} alt="" draggable={false} className="size-full object-cover" />
			) : (
				<Skeleton className="size-full rounded-none" />
			)}
			<span className="absolute inset-0 flex items-center justify-center">
				<span className="flex size-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition-transform duration-100 group-hover:scale-105">
					{requested ? (
						<span className="size-3 animate-pulse rounded-full bg-white" />
					) : (
						<PlayIcon className="size-4 translate-x-px fill-current" />
					)}
				</span>
			</span>
		</button>
	);
});
